import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COURSES } from '../home/components/TrainingCoursesBanner';
import CoursesSEOHead from './CoursesSEOHead';
import { useLang } from '../../hooks/useLang';
import { LangProvider } from '../../hooks/useLang';

const COURSES_LABELS: Record<string, Record<string, string>> = {
  backHome: { zh: '返回首页', en: 'Back to Home', fr: 'Retour à l\'accueil', es: 'Volver al inicio', pt: 'Voltar ao início', ja: 'ホームに戻る', de: 'Zurück zur Startseite', ko: '홈으로 돌아가기', ar: 'العودة إلى الرئيسية', ru: 'На главную' },
  badge: { zh: 'RWA专业培训课程体系', en: 'RWA Professional Training System', fr: 'Système de formation professionnelle RWA', es: 'Sistema de formación profesional RWA', pt: 'Sistema de treinamento profissional RWA', ja: 'RWAプロフェッショナルトレーニングシステム', de: 'RWA Professionelles Trainingssystem', ko: 'RWA 전문 교육 시스템', ar: 'نظام التدريب المهني RWA', ru: 'Профессиональная система обучения RWA' },
  titleLine1: { zh: '区块链 & RWA 全系列', en: 'Blockchain & RWA Complete Series', fr: 'Blockchain & RWA Série Complète', es: 'Blockchain & RWA Serie Completa', pt: 'Blockchain & RWA Série Completa', ja: 'ブロックチェーン & RWA 完全シリーズ', de: 'Blockchain & RWA Komplette Serie', ko: '블록체인 & RWA 전체 시리즈', ar: 'سلسلة بلوكتشين و RWA الكاملة', ru: 'Полная серия Блокчейн & RWA' },
  titleLine2: { zh: '专业课程中心', en: 'Professional Course Center', fr: 'Centre de cours professionnel', es: 'Centro de cursos profesionales', pt: 'Centro de cursos profissionais', ja: 'プロフェッショナルコースセンター', de: 'Professionelles Kurszentrum', ko: '전문 강좌 센터', ar: 'مركز الدورات المهنية', ru: 'Профессиональный центр курсов' },
  subtitle: { zh: '8门系统课程 · 从区块链零基础到RWA合规发行全覆盖 · 业内专家主讲 · 视频课持续更新中', en: '8 systematic courses · From blockchain basics to RWA compliant issuance · Industry experts · Video courses continuously updated', fr: '8 cours systématiques · Des bases de la blockchain à l\'émission conforme RWA · Experts du secteur · Cours vidéo continuellement mis à jour', es: '8 cursos sistemáticos · Desde los fundamentos de blockchain hasta la emisión conforme RWA · Expertos del sector · Cursos en video actualizados continuamente', pt: '8 cursos sistemáticos · Dos fundamentos do blockchain à emissão conforme RWA · Especialistas do setor · Cursos em vídeo continuamente atualizados', ja: '8つの体系的なコース · ブロックチェーンの基礎からRWA準拠発行まで · 業界専門家 · ビデオコース継続更新中', de: '8 systematische Kurse · Von Blockchain-Grundlagen bis zur RWA-konformen Ausgabe · Branchenexperten · Videokurse werden kontinuierlich aktualisiert', ko: '8개 체계적인 강좌 · 블록체인 기초부터 RWA 준수 발행까지 · 업계 전문가 · 동영상 강좌 지속 업데이트', ar: '8 دورات منهجية · من أساسيات البلوكتشين إلى إصدار RWA المتوافق · خبراء الصناعة · دورات فيديو محدثة باستمرار', ru: '8 систематических курсов · От основ блокчейна до соответствующего выпуска RWA · Отраслевые эксперты · Видеокурсы постоянно обновляются' },
  statCourses: { zh: '专业课程', en: 'Courses', fr: 'Cours', es: 'Cursos', pt: 'Cursos', ja: 'コース', de: 'Kurse', ko: '강좌', ar: 'دورات', ru: 'Курсов' },
  statHours: { zh: '课时', en: 'Hours', fr: 'Heures', es: 'Horas', pt: 'Horas', ja: '時間', de: 'Stunden', ko: '시간', ar: 'ساعات', ru: 'Часов' },
  statStudents: { zh: '学员', en: 'Students', fr: 'Étudiants', es: 'Estudiantes', pt: 'Alunos', ja: '受講者', de: 'Studenten', ko: '수강생', ar: 'طلاب', ru: 'Студентов' },
  statLifetime: { zh: '购买后', en: 'After Purchase', fr: 'Après achat', es: 'Tras la compra', pt: 'Após compra', ja: '購入後', de: 'Nach Kauf', ko: '구매 후', ar: 'بعد الشراء', ru: 'После покупки' },
  statLifetimeVal: { zh: '永久有效', en: 'Lifetime Access', fr: 'Accès à vie', es: 'Acceso de por vida', pt: 'Acesso vitalício', ja: '永久アクセス', de: 'Lebenslanger Zugang', ko: '평생 이용', ar: 'وصول مدى الحياة', ru: 'Пожизненный доступ' },
  learningPath: { zh: '推荐学习路径：', en: 'Recommended Learning Path:', fr: 'Parcours d\'apprentissage recommandé:', es: 'Ruta de aprendizaje recomendada:', pt: 'Caminho de aprendizado recomendado:', ja: '推奨学習パス：', de: 'Empfohlener Lernpfad:', ko: '추천 학습 경로:', ar: 'مسار التعلم الموصى به:', ru: 'Рекомендуемый путь обучения:' },
  catAll: { zh: '全部课程', en: 'All Courses', fr: 'Tous les cours', es: 'Todos los cursos', pt: 'Todos os cursos', ja: 'すべてのコース', de: 'Alle Kurse', ko: '전체 강좌', ar: 'جميع الدورات', ru: 'Все курсы' },
  catBlockchain: { zh: '区块链基础', en: 'Blockchain Basics', fr: 'Bases de la Blockchain', es: 'Fundamentos de Blockchain', pt: 'Fundamentos de Blockchain', ja: 'ブロックチェーン基礎', de: 'Blockchain-Grundlagen', ko: '블록체인 기초', ar: 'أساسيات البلوكتشين', ru: 'Основы блокчейна' },
  catRwa: { zh: 'RWA实操', en: 'RWA Practice', fr: 'Pratique RWA', es: 'Práctica RWA', pt: 'Prática RWA', ja: 'RWA実践', de: 'RWA-Praxis', ko: 'RWA 실습', ar: 'ممارسة RWA', ru: 'Практика RWA' },
  catCompliance: { zh: '合规监管', en: 'Compliance & Regulation', fr: 'Conformité & Réglementation', es: 'Cumplimiento y Regulación', pt: 'Conformidade e Regulação', ja: 'コンプライアンス・規制', de: 'Compliance & Regulierung', ko: '컴플라이언스 및 규제', ar: 'الامتثال والتنظيم', ru: 'Соответствие и регулирование' },
  catDefi: { zh: 'DeFi融合', en: 'DeFi Integration', fr: 'Intégration DeFi', es: 'Integración DeFi', pt: 'Integração DeFi', ja: 'DeFi統合', de: 'DeFi-Integration', ko: 'DeFi 통합', ar: 'تكامل DeFi', ru: 'Интеграция DeFi' },
  lessons: { zh: '节课', en: 'lessons', fr: 'leçons', es: 'lecciones', pt: 'aulas', ja: 'レッスン', de: 'Lektionen', ko: '강의', ar: 'دروس', ru: 'уроков' },
  students: { zh: '人', en: 'students', fr: 'étudiants', es: 'estudiantes', pt: 'alunos', ja: '人', de: 'Studenten', ko: '명', ar: 'طالب', ru: 'студентов' },
  viewCourse: { zh: '查看完整课程大纲 →', en: 'View Full Curriculum →', fr: 'Voir le programme complet →', es: 'Ver plan de estudios completo →', pt: 'Ver currículo completo →', ja: '完全なカリキュラムを見る →', de: 'Vollständigen Lehrplan ansehen →', ko: '전체 커리큘럼 보기 →', ar: 'عرض المنهج الكامل →', ru: 'Посмотреть полную программу →' },
};

const LEARNING_PATH_NAMES: Record<string, string[]> = {
  zh: ['区块链核心原理精讲', 'ERC标准全体系', 'RWA基础体系', '香港RWA合规全攻略', '中国资产RWA化实操', '碳资产RWA全流程'],
  en: ['Blockchain Core Principles', 'ERC Standards System', 'RWA Fundamentals', 'HK RWA Compliance', 'China Asset RWA Practice', 'Carbon Asset RWA Process'],
  fr: ['Principes fondamentaux Blockchain', 'Système de normes ERC', 'Fondamentaux RWA', 'Conformité RWA HK', 'Pratique RWA actifs chinois', 'Processus RWA actifs carbone'],
  es: ['Principios básicos Blockchain', 'Sistema de estándares ERC', 'Fundamentos RWA', 'Cumplimiento RWA HK', 'Práctica RWA activos chinos', 'Proceso RWA activos de carbono'],
  pt: ['Princípios básicos Blockchain', 'Sistema de padrões ERC', 'Fundamentos RWA', 'Conformidade RWA HK', 'Prática RWA ativos chineses', 'Processo RWA ativos de carbono'],
  ja: ['ブロックチェーン基礎原理', 'ERC標準体系', 'RWA基礎体系', '香港RWAコンプライアンス', '中国資産RWA実践', 'カーボン資産RWAプロセス'],
  de: ['Blockchain-Grundprinzipien', 'ERC-Standards-System', 'RWA-Grundlagen', 'HK RWA-Compliance', 'China Asset RWA-Praxis', 'Carbon Asset RWA-Prozess'],
  ko: ['블록체인 핵심 원리', 'ERC 표준 체계', 'RWA 기초 체계', '홍콩 RWA 컴플라이언스', '중국 자산 RWA 실습', '탄소 자산 RWA 프로세스'],
  ar: ['مبادئ البلوكتشين الأساسية', 'نظام معايير ERC', 'أساسيات RWA', 'امتثال RWA في هونغ كونغ', 'ممارسة RWA للأصول الصينية', 'عملية RWA للأصول الكربونية'],
  ru: ['Основные принципы блокчейна', 'Система стандартов ERC', 'Основы RWA', 'Соответствие RWA в Гонконге', 'Практика RWA китайских активов', 'Процесс RWA углеродных активов'],
};

function CoursesContent() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [activeCategory, setActiveCategory] = useState('all');
  const getLabel = (key: string) => COURSES_LABELS[key]?.[lang] ?? COURSES_LABELS[key]?.['en'] ?? key;

  const categories = [
    { id: 'all', label: getLabel('catAll') },
    { id: 'blockchain', label: getLabel('catBlockchain') },
    { id: 'rwa', label: getLabel('catRwa') },
    { id: 'compliance', label: getLabel('catCompliance') },
    { id: 'defi', label: getLabel('catDefi') },
  ];

  const filtered = activeCategory === 'all' ? COURSES : COURSES.filter((c) => c.category === activeCategory);
  const learningPath = LEARNING_PATH_NAMES[lang] ?? LEARNING_PATH_NAMES['en'];

  return (
    <div className="min-h-screen bg-gray-50">
      <CoursesSEOHead />
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-[#1a0a3e] py-16 px-4 lg:px-8">
        <div className="max-w-[1440px] mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>{getLabel('backHome')}
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-400/20 rounded-xl">
              <i className="ri-graduation-cap-line text-amber-400 text-lg"></i>
            </div>
            <span className="text-amber-400 text-sm font-semibold">{getLabel('badge')}</span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-black text-white mb-4">
            {getLabel('titleLine1')}<br />
            <span className="text-amber-400">{getLabel('titleLine2')}</span>
          </h1>
          <p className="text-white/60 text-base max-w-2xl leading-relaxed">{getLabel('subtitle')}</p>
          <div className="flex flex-wrap gap-4 mt-6">
            {[
              { num: '8', label: getLabel('statCourses') },
              { num: '102+', label: getLabel('statHours') },
              { num: '13,119+', label: getLabel('statStudents') },
              { num: getLabel('statLifetimeVal'), label: getLabel('statLifetime') },
            ].map((s) => (
              <div key={s.label} className="bg-white/10 rounded-xl px-4 py-2">
                <div className="text-white font-black text-lg leading-none">{s.num}</div>
                <div className="text-white/50 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Learning Path Banner */}
      <div className="bg-amber-50 border-b border-amber-200/60 px-4 lg:px-8 py-4">
        <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <i className="ri-route-line text-amber-600 text-base"></i>
            <span className="text-amber-800 font-bold text-sm">{getLabel('learningPath')}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-amber-700">
            {learningPath.map((name, i) => (
              <span key={i} className="flex items-center gap-2">
                <span className="bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-full font-medium whitespace-nowrap">{name}</span>
                {i < learningPath.length - 1 && <i className="ri-arrow-right-s-line text-amber-400"></i>}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-10">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? 'bg-[#7C3AED] text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#7C3AED]/40'
              }`}
            >
              {cat.label}
              <span className={`ml-1.5 text-xs ${activeCategory === cat.id ? 'opacity-75' : 'text-gray-400'}`}>
                ({cat.id === 'all' ? COURSES.length : COURSES.filter((c) => c.category === cat.id).length})
              </span>
            </button>
          ))}
        </div>

        {/* Course Cards */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((course) => (
            <div
              key={course.id}
              className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#7C3AED]/30 transition-all cursor-pointer group"
              onClick={() => navigate(`/courses/${course.id}`)}
            >
              {/* Cover */}
              <div className="h-32 flex items-center justify-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${course.coverColor}, ${course.coverColor}88)` }}>
                <i className={`${course.icon} text-white/20 text-7xl absolute`}></i>
                <div className="relative z-10 text-center px-6">
                  <div className="text-white/60 text-xs font-bold mb-1">COURSE {course.num}</div>
                  <h3 className="text-white font-black text-base leading-tight">{course.title}</h3>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="text-xs px-2.5 py-1 rounded-full font-semibold text-white" style={{ background: course.badgeColor }}>{course.badge}</span>
                </div>
              </div>

              <div className="p-5">
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{course.subtitle}</p>

                {/* Meta */}
                <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                  <span className="flex items-center gap-1.5"><i className="ri-time-line text-[#7C3AED]"></i>{course.duration}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-play-list-line text-[#7C3AED]"></i>{course.lessons}{getLabel('lessons')}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-user-3-line text-[#7C3AED]"></i>{course.students}{getLabel('students')}</span>
                </div>

                {/* Highlights */}
                <div className="space-y-1.5 mb-4">
                  {course.highlights.slice(0, 3).map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center bg-[#7C3AED]/10 rounded-full shrink-0">
                        <i className="ri-check-line text-[#7C3AED] text-[10px]"></i>
                      </div>
                      {h}
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: course.levelColor + '20', color: course.levelColor }}>{course.level}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{course.categoryLabel}</span>
                  </div>
                  <div className="text-[#7C3AED] font-black text-lg">{course.price}</div>
                </div>

                <button className="mt-3 w-full py-2.5 border border-[#7C3AED]/30 text-[#7C3AED] rounded-xl text-sm font-semibold hover:bg-[#7C3AED] hover:text-white transition-all group-hover:bg-[#7C3AED] group-hover:text-white cursor-pointer whitespace-nowrap">
                  {getLabel('viewCourse')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CoursesPage() {
  return (
    <LangProvider>
      <CoursesContent />
    </LangProvider>
  );
}
